import { urlApi } from '@/utils/url';
import { NetworkLevel } from '@/utils/network/score';

export interface NetworkDailyRow {
    user_email: string;
    day: string;
    minutes_measured: number;
    samples: number;
    timeouts: number;
    reconnects: number;
    rtt_avg: number | null;
    rtt_p95: number | null;
    rtt_max: number | null;
    jitter_avg: number | null;
    jitter_max: number | null;
    udp_loss_avg: number | null;
    udp_loss_max: number | null;
    udp_jitter_avg: number | null;
    score_avg: number | null;
    score_worst: number | null;
    score_best: number | null;
    level: NetworkLevel;
}

export interface NetworkExtreme {
    user_email: string;
    score_avg: number | null;
    score_worst: number | null;
    rtt_avg: number | null;
    udp_loss_avg: number | null;
    minutes_measured: number;
    level: NetworkLevel;
}

export interface NetworkSummaryDay {
    day: string;
    users: number;
    best: NetworkExtreme | null;
    worst: NetworkExtreme | null;
}

export interface NetworkSummary {
    days: NetworkSummaryDay[];
    minMinutes: number;
}

export interface NetworkBucket {
    bucket_at: string;
    samples: number;
    timeouts: number;
    reconnects: number;
    rtt_avg: number | null;
    rtt_max: number | null;
    jitter_avg: number | null;
    udp_probe_ok: boolean;
    udp_rtt_avg: number | null;
    udp_jitter_avg: number | null;
    udp_loss_pct: number | null;
    ice_candidate_type: string | null;
    effective_type: string | null;
    downlink: number | null;
    score: number | null;
}

export interface NetworkStatus {
    users_total: number;
    users_degraded: number;
    networks_total: number;
    networks_degraded: number;
    score_median: number | null;
    score_worst: number | null;
    level: NetworkLevel;
}

export interface NetworkIncident {
    day: string;
    started_at: string;
    ended_at: string;
    windows: number;
    users_total: number;
    users_degraded: number;
    networks_total: number;
    networks_degraded: number;
    scope: 'network' | 'platform';
    worst_score: number | null;
    level: NetworkLevel;
}

/**
 * Que se observo en cada tipo de episodio.
 *
 * Se describe el hecho medido y se deja la conclusion al que lee. Afirmar
 * de quien es el problema es una conclusion que este dato sugiere pero no
 * demuestra, y conviene no dejarla escrita como si fuera un veredicto.
 */
export const INCIDENT_SCOPE_MEANING: Record<NetworkIncident['scope'], string> = {
    network: 'Los afectados salían por la misma red.',
    platform: 'Los afectados salían por redes distintas, cada una con su proveedor, '
        + 'y se degradaron en la misma ventana de tiempo.',
};

async function request<T>(path: string, params: Record<string, unknown> = {}): Promise<T> {
    const token = sessionStorage.getItem('token') || '';
    const url = new URL(urlApi + path);

    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            url.searchParams.append(key, String(value));
        }
    });

    const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${token}`,
        },
    });

    const body = await response.json();

    if (!response.ok) {
        throw new Error(`Error ${response.status}: ${JSON.stringify(body)}`);
    }

    return body as T;
}

/**
 * Filas de un dia para todo el customer. El dominio lo resuelve el backend
 * desde el token, no se puede pedir el histórico de otro customer.
 */
export async function fetchNetworkDaily(day?: string): Promise<NetworkDailyRow[]> {
    const body = await request<{ data?: NetworkDailyRow[] }>('/network/daily', { day });

    return body?.data || [];
}

/** Mejor y peor usuario por dia, para los ultimos `days` dias. */
export async function fetchNetworkSummary(days = 30): Promise<NetworkSummary> {
    const body = await request<{ data?: NetworkSummaryDay[]; min_minutes?: number }>(
        '/network/summary',
        { days },
    );

    return {
        days: body?.data || [],
        minMinutes: body?.min_minutes ?? 30,
    };
}

/** Ventanas propias del usuario, para el grafico en vivo. */
export async function fetchNetworkBuckets(hours = 2): Promise<NetworkBucket[]> {
    const body = await request<{ data?: NetworkBucket[] }>('/network/buckets', { hours });

    return body?.data || [];
}

/** Foto del equipo ahora mismo: cuantos midiendo y cuantos con problemas. */
export async function fetchNetworkStatus(): Promise<NetworkStatus | null> {
    const body = await request<{ data?: NetworkStatus }>('/network/status');

    return body?.data || null;
}

/** Momentos del dia en que varios se degradaron a la vez. */
export async function fetchNetworkIncidents(day?: string): Promise<NetworkIncident[]> {
    const body = await request<{ data?: NetworkIncident[] }>('/network/incidents', { day });

    return body?.data || [];
}
